import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, ArrowUpRight, ArrowDownRight, Minus, ShieldAlert, CheckCircle2, XCircle, AlertTriangle, Target, Ban, Hourglass, Wallet } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DIR = {
  UP: { icon: ArrowUpRight, cls: "text-emerald-400", bg: "bg-emerald-500/10" },
  DOWN: { icon: ArrowDownRight, cls: "text-red-400", bg: "bg-red-500/10" },
  FLAT: { icon: Minus, cls: "text-amber-400", bg: "bg-amber-500/10" },
};
const ACTION_STYLE = {
  "BUY NOW": "bg-emerald-500 text-[#07090E]", "BUY ON TRIGGER": "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  "SELL NOW": "bg-red-500 text-white", "SELL ON TRIGGER": "bg-red-500/20 text-red-300 border border-red-500/40",
  HOLD: "bg-amber-500/15 text-amber-300 border border-amber-500/40", WAIT: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
  "NO TRADE": "bg-white/10 text-slate-200 border border-white/20",
};
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en", { maximumFractionDigits: 4 }));

export const DetectionBanner = ({ detection, onConfirm }) => {
  const [sym, setSym] = useState(detection?.symbol || "");
  const [tf, setTf] = useState((detection?.timeframe || "").toString().toLowerCase());
  if (!detection?.needs_confirmation) return null;
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="detection-banner">
      <p className="text-sm font-semibold text-amber-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {detection.message}</p>
      <p className="text-xs text-slate-400 mt-1">Read from chart: symbol <span className="font-mono-num">{detection.symbol || "?"}</span> ({detection.symbol_confidence ?? "?"}%), timeframe <span className="font-mono-num">{detection.timeframe || "?"}</span> ({detection.timeframe_confidence ?? "?"}%), last candle {detection.last_candle_time || "?"}, last price {detection.last_price ?? "?"}. Never guessed silently — please confirm.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <Input value={sym} onChange={(e) => setSym(e.target.value.toUpperCase())} placeholder="Symbol" className="h-9 w-32 bg-white/5 border-white/10 font-mono-num" data-testid="detect-symbol-input" />
        <select value={tf} onChange={(e) => setTf(e.target.value)} className="h-9 rounded-md bg-white/5 border border-white/10 px-2 text-sm font-mono-num" data-testid="detect-timeframe-select">
          <option value="">timeframe…</option>
          {["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
        </select>
        <Button size="sm" onClick={() => onConfirm(sym, tf)} disabled={!sym || !tf} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] h-9" data-testid="detect-confirm-button">Confirm &amp; re-analyze</Button>
      </div>
    </div>
  );
};

const ForecastTable = ({ forecast }) => {
  if (!forecast?.candles?.length) return null;
  return (
    <div data-testid="forecast-table">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Next {forecast.candles.length} candles · {forecast.timeframe}</p>
        <p className="text-xs font-mono-num"><span className="text-emerald-400">UP {forecast.up_pct_next3}%</span> · <span className="text-red-400">DOWN {forecast.down_pct_next3}%</span> <span className="text-slate-500">(next 3)</span></p>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left"><th className="px-1 py-1">Time</th><th className="px-1 py-1">Dir</th><th className="px-1 py-1">Low</th><th className="px-1 py-1">High</th><th className="px-1 py-1">Up%</th><th className="px-1 py-1">Note</th></tr></thead>
          <tbody>
            {forecast.candles.map((c) => { const d = DIR[c.direction] || DIR.FLAT; return (
              <tr key={c.index} className="border-t border-white/5" data-testid={`forecast-candle-${c.index}`}>
                <td className="px-1 py-1.5 font-mono-num">{c.time}</td>
                <td className={`px-1 py-1.5 font-semibold ${d.cls}`}><span className="inline-flex items-center gap-1"><d.icon className="w-3.5 h-3.5" />{c.direction}</span></td>
                <td className="px-1 py-1.5 font-mono-num text-slate-300">{fmt(c.expected_low)}</td>
                <td className="px-1 py-1.5 font-mono-num text-slate-300">{fmt(c.expected_high)}</td>
                <td className="px-1 py-1.5 font-mono-num">{c.probability_up}%</td>
                <td className="px-1 py-1.5 text-slate-400 text-xs">{c.note}</td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      {forecast.moves?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {forecast.moves.map((m, i) => <li key={i} className="text-xs text-slate-300" data-testid={`forecast-move-${i}`}><span className="font-mono-num text-slate-400">{m.from_time} → {m.to_time}</span> ({m.candles} candles) <span className={m.direction === "DOWN" ? "text-red-400" : "text-emerald-400"}>{m.direction}</span> toward <span className="font-mono-num">{fmt(m.target)}</span>{m.then ? `, then ${m.then}` : ""}</li>)}
        </ul>
      )}
      {forecast.summary && <p className="text-xs text-slate-400 mt-2">{forecast.summary}</p>}
    </div>
  );
};

const Sizing = ({ risk }) => {
  const { user, setUser } = useAuth();
  const [cap, setCap] = useState(user?.capital ?? "");
  const s = risk?.sizing;
  const save = async () => {
    const v = parseFloat(cap);
    if (!v || v <= 0) return;
    try { await api.patch("/auth/me/preferences", { capital: v }); setUser((u) => ({ ...u, capital: v })); toast.success("Capital saved — sizing uses it from the next analysis"); } catch { toast.error("Could not save"); }
  };
  if (!s) return null;
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3" data-testid="risk-sizing">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Risk {risk.suggested_risk_pct} · R:R <span className={`font-mono-num ${risk.rr != null && risk.rr >= risk.min_rr ? "text-emerald-400" : "text-red-400"}`}>{risk.rr_display}</span></p>
        <div className="flex items-center gap-1.5">
          <Input value={cap} onChange={(e) => setCap(e.target.value)} type="number" placeholder="Your capital" className="h-8 w-32 bg-white/5 border-white/10 text-xs font-mono-num" data-testid="capital-input" />
          <Button size="sm" variant="outline" onClick={save} className="h-8 border-white/15 text-xs" data-testid="capital-save">Save</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {s.options.map((o) => (
          <div key={o.risk_pct} className="text-xs" data-testid={`sizing-${o.risk_pct}pct`}>
            <p className="text-slate-400">Risk {o.risk_pct}% {s.capital_assumed ? "(of 10,000)" : ""}</p>
            <p className="font-mono-num"><span className="font-semibold">{o.quantity}</span> units · risk {fmt(o.risk_amount)} · {fmt(s.risk_per_unit)}/unit</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const InstructionCard = ({ instruction, signal, onSpeak }) => {
  const [state, setState] = useState(null);
  const seen = useRef(new Set());
  const [deciding, setDeciding] = useState(false);
  const sigId = signal?.signal_id;
  useEffect(() => {
    if (!sigId) return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/signals/${sigId}/state`);
        if (stop) return;
        setState(data);
        (data.events || []).forEach((e) => {
          if (!seen.current.has(e.id)) {
            seen.current.add(e.id);
            if (seen.current.size > 1 || data.events.length === 1) { toast(e.text, { duration: 8000 }); onSpeak?.(e.text, e.id); }
          }
        });
        if (data.status === "resolved") stop = true;
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(() => { if (!stop) poll(); }, 30000);
    return () => { stop = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigId]);

  const decide = async (decision) => {
    setDeciding(true);
    try { const { data } = await api.post(`/signals/${sigId}/decision`, { decision, quantity: 1 }); setState((s) => ({ ...(s || {}), ...data })); toast.success(decision === "taken" ? "Logged to your journal — I'll guide the exit live" : "Skipped — I'll still track the outcome for accuracy"); }
    catch { toast.error("Could not save decision"); } finally { setDeciding(false); }
  };

  if (!instruction) return null;
  const ins = instruction;
  const blocked = ins.action === "NO TRADE" || ins.action === "WAIT";
  const Row = ({ k, v, testId }) => (v ? <div className="flex gap-3 text-sm" data-testid={testId}><span className="w-24 shrink-0 text-[10px] uppercase tracking-wider text-slate-500 pt-0.5">{k}</span><span className="text-slate-200 min-w-0">{v}</span></div> : null);
  return (
    <div className="glass rounded-2xl p-5 space-y-3" data-testid="instruction-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className={`font-display text-lg font-extrabold px-3 py-1 rounded-full ${ACTION_STYLE[ins.action] || ACTION_STYLE.WAIT}`} data-testid="instruction-action">
          {ins.action === "NO TRADE" ? <Ban className="inline w-4 h-4 mr-1 -mt-0.5" /> : ins.action === "WAIT" ? <Hourglass className="inline w-4 h-4 mr-1 -mt-0.5" /> : <Target className="inline w-4 h-4 mr-1 -mt-0.5" />}{ins.action}
        </span>
        {state?.live_state && <span className="text-xs font-mono-num text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-2 py-0.5" data-testid="instruction-live-state">live: {state.live_state}{state.decision ? ` · ${state.decision}` : ""}</span>}
      </div>
      {blocked && ins.blockers?.length > 0 && (
        <ul className="space-y-1" data-testid="instruction-blockers">
          {ins.blockers.map((b, i) => <li key={i} className="flex items-start gap-1.5 text-sm text-amber-300"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /><span><span className="font-semibold">{b.kind}:</span> {b.reason}</span></li>)}
          {ins.ai_action && ins.ai_action !== ins.action && <li className="text-xs text-slate-500">AI leaned “{ins.ai_action}” — overridden by the risk engine. Levels below are for reference only.</li>}
        </ul>
      )}
      <Row k="When" v={ins.when} testId="instruction-when" />
      <Row k="Trigger" v={ins.trigger} testId="instruction-trigger" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="instruction-levels">
        {[["Entry", ins.entry, "text-white"], ["Stop loss", ins.stop_loss, "text-red-400"], ["Target 1", ins.target1, "text-emerald-400"], ["Target 2", ins.target2, "text-emerald-300"]].map(([k, v, c]) => (
          <div key={k} className="rounded-lg bg-white/5 border border-white/10 p-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">{k}</p><p className={`font-mono-num font-bold ${c}`}>{fmt(v)}</p></div>
        ))}
      </div>
      {ins.levels_note && <p className="text-xs text-cyan-300 flex items-start gap-1.5" data-testid="levels-note"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{ins.levels_note}</p>}
      {ins.exit_rules?.length > 0 && <div data-testid="instruction-exit-rules"><p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Exit rules</p><ul className="text-sm text-slate-300 space-y-0.5">{ins.exit_rules.map((r, i) => <li key={i}>• {r}</li>)}</ul></div>}
      <Row k="Cancel if" v={ins.cancel_if} testId="instruction-cancel" />
      <Row k="Valid until" v={ins.valid_until} testId="instruction-valid-until" />
      <Row k="Hold" v={ins.hold_duration} />
      <Sizing risk={ins.risk} />
      <p className="text-sm font-semibold text-amber-300 flex items-start gap-1.5" data-testid="invalidation-line"><XCircle className="w-4 h-4 shrink-0 mt-0.5" />{ins.invalidation_line}</p>
      {state?.events?.length > 0 && (
        <ul className="space-y-1 border-t border-white/5 pt-2" data-testid="instruction-events">
          {state.events.map((e) => <li key={e.id} className="text-xs text-cyan-300 font-mono-num">• {e.text}</li>)}
        </ul>
      )}
      {sigId && !blocked && (
        <div className="flex gap-2 pt-1">
          {!state?.decision ? (
            <>
              <Button size="sm" disabled={deciding} onClick={() => decide("taken")} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="trade-taken-button"><CheckCircle2 className="w-4 h-4 mr-1" /> I took this trade</Button>
              <Button size="sm" variant="outline" disabled={deciding} onClick={() => decide("skipped")} className="border-white/15 text-slate-300" data-testid="trade-skipped-button">Skipped</Button>
            </>
          ) : <p className="text-xs text-slate-400" data-testid="trade-decision">{state.decision === "taken" ? "Trade logged to journal · live exit guidance active" : "Skipped · outcome still tracked for accuracy"}</p>}
        </div>
      )}
      <p className="text-[11px] text-slate-500" data-testid="instruction-disclaimer">{ins.disclaimer}</p>
    </div>
  );
};

export const ForecastReport = ({ result, signal, onConfirm, onSpeak }) => {
  const r = result;
  if (!r) return null;
  const tf = r.forecast?.timeframe || r.timeframe;
  return (
    <div className="space-y-4" data-testid="forecast-report">
      <DetectionBanner detection={r.detection} onConfirm={onConfirm} />
      <InstructionCard instruction={r.instruction} signal={signal} onSpeak={onSpeak} />
      <div className="glass rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold">Time-based forecast</p>
          <div className="flex items-center gap-2">
            {tf && <span className="text-xs font-mono-num bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 rounded-full px-2.5 py-0.5" data-testid="timeframe-badge">{String(tf).toUpperCase()}</span>}
            <span className="text-xs font-mono-num"><span className="text-emerald-400">UP {r.forecast?.overall_up ?? "—"}%</span> / <span className="text-red-400">DOWN {r.forecast?.overall_down ?? "—"}%</span></span>
          </div>
        </div>
        <ForecastTable forecast={r.forecast} />
        <div className="grid sm:grid-cols-2 gap-3 min-w-0">
          <div className="min-w-0" data-testid="report-reasons"><p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Why · top reasons</p><ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside break-words">{(r.reasons || []).map((x, i) => <li key={i}>{x}</li>)}</ol></div>
          <div className="min-w-0" data-testid="report-risks"><p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Top risks</p><ul className="text-sm text-red-300/90 space-y-1 break-words">{(r.risks || []).map((x, i) => <li key={i}>• {x}</li>)}</ul></div>
        </div>
        {r.events?.events?.length > 0 && (
          <div className={`rounded-lg border px-3 py-2 text-xs ${r.events.blocking ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-white/10 bg-white/5 text-slate-300"}`} data-testid="events-panel">
            <p className="font-semibold mb-1">Events &amp; news (±24h){r.events.warning ? ` — ${r.events.warning}` : ""}</p>
            <ul className="space-y-0.5">{r.events.events.map((e, i) => <li key={i}>• {e.title} <span className="text-slate-500 font-mono-num">({e.hours_away >= 0 ? `in ${Math.round(e.hours_away)}h` : `${Math.round(-e.hours_away)}h ago`})</span></li>)}</ul>
          </div>
        )}
        <p className="text-[11px] text-slate-500" data-testid="honesty-note">{r.probability?.backtested ? `Backtested: ${r.probability.display}` : "AI confidence estimate — not backtested"}. {r.honesty_note}</p>
      </div>
    </div>
  );
};
